import {
  CollectionDocument,
  CollectionMembershipDocument,
  CollectionSummary,
  InventoryItemSort,
} from '@qkindex/contracts';
import { Item } from '../../domain/item';
import { Location } from '../../domain/location';
import type {
  InventoryMetadataCache,
  CachedInventory,
} from './inventoryMetadataCache';
import type { InventoryChange } from './inventoryInvalidation';
import type { InventoryPage } from '../../repositories/multiCollectionInventoryRepository';

export type InventoryResource = 'items' | 'locations';
export interface InventoryResourceSnapshot<T> {
  dataRevision: number;
  records: T[];
  nextCursor?: string;
  orderedThrough?: T;
  initialized: boolean;
  /** A full online read completed in this session; disk cache alone is insufficient. */
  confirmedOnline?: boolean;
  busy: boolean;
  refreshing: boolean;
  failed: boolean;
}
export interface CollectionInventorySnapshot {
  status: 'loading' | 'ready' | 'error' | 'unavailable';
  summary?: CollectionSummary;
  offline?: boolean;
  items: InventoryResourceSnapshot<Item>;
  locations: InventoryResourceSnapshot<Location>;
}
interface Resource {
  snapshot: InventoryResourceSnapshot<Item | Location>;
  version: number;
  updatedAt: number;
  patches: Map<string, Item | Location | undefined>;
  patchVersions: Map<string, number>;
}
interface Entry {
  id: string;
  snapshot: CollectionInventorySnapshot;
  resources: Record<InventoryResource, Resource>;
  consumers: Map<() => void, readonly InventoryResource[]>;
  membership?: CollectionMembershipDocument;
  collection?: CollectionDocument;
  membershipReady: boolean;
  collectionReady: boolean;
  authorityFailed: boolean;
  identity?: string;
  stops: Array<() => void>;
  epoch: number;
  dirty: Set<InventoryResource>;
  wanted: Set<InventoryResource>;
  running: Set<InventoryResource>;
  pending?: Promise<void>;
  itemSort?: InventoryItemSort;
  hydrated?: boolean;
  hydrating?: Promise<void>;
}
interface Dependencies {
  membership(
    id: string,
    next: (value: CollectionMembershipDocument | undefined) => void,
    error: () => void,
  ): () => void;
  collection(
    id: string,
    next: (value: CollectionDocument | undefined) => void,
    error: () => void,
  ): () => void;
  page(
    id: string,
    kind: InventoryResource,
    cursor?: string,
    sort?: InventoryItemSort,
  ): Promise<InventoryPage<Item | Location>>;
  demandChanged(): void;
  cache?: InventoryMetadataCache;
  record?(
    id: string,
    kind: InventoryResource,
    recordId: string,
  ): Promise<Item | Location | undefined>;
}
const empty = (): Resource => ({
  snapshot: {
    dataRevision: 0,
    records: [],
    initialized: false,
    busy: false,
    refreshing: false,
    failed: false,
  },
  version: 0,
  updatedAt: 0,
  patches: new Map(),
  patchVersions: new Map(),
});
// Preserve record order while letting local mutations override any older snapshot.
const mergeRecords = (
  records: Array<Item | Location>,
  patches: Resource['patches'],
) => {
  const merged = new Map(records.map((record) => [record.id, record]));
  patches.forEach((record, id) => {
    if (record) merged.set(id, record);
    else merged.delete(id);
  });
  return [...merged.values()];
};
const kinds: InventoryResource[] = ['items', 'locations'];
export const INVENTORY_FRESH_MS = 30_000;
export const INVENTORY_REFRESH_MS = 5 * 60_000;
const canLoad = () =>
  (typeof navigator === 'undefined' || navigator.onLine !== false) &&
  (typeof document === 'undefined' || document.visibilityState !== 'hidden');

/** Shared bounded pages. A cache hit never substitutes for a live authority subscription. */
export class CollectionInventoryStore {
  private entries = new Map<string, Entry>();
  private disposed = false;
  private activeRequests = 0;
  private waiting: Array<() => void> = [];
  private wakeups = new Set<() => void>();
  revision = 0;
  constructor(private dependencies: Dependencies) {}

  private entry(id: string): Entry {
    let entry = this.entries.get(id);
    if (!entry) {
      const resources = { items: empty(), locations: empty() };
      entry = {
        id,
        resources,
        snapshot: {
          status: 'loading',
          items: resources.items.snapshot as InventoryResourceSnapshot<Item>,
          locations: resources.locations
            .snapshot as InventoryResourceSnapshot<Location>,
        },
        consumers: new Map(),
        membershipReady: false,
        collectionReady: false,
        authorityFailed: false,
        stops: [],
        epoch: 0,
        dirty: new Set(),
        wanted: new Set(),
        running: new Set(),
      };
      this.entries.set(id, entry);
    }
    return entry;
  }
  getSnapshot = (id: string) => this.entry(id).snapshot;
  getRevision = () => this.revision;
  get hasConsumers() {
    return [...this.entries.values()].some((entry) => entry.consumers.size > 0);
  }

  private publish(entry: Entry) {
    entry.snapshot = {
      ...entry.snapshot,
      offline: typeof navigator !== 'undefined' && navigator.onLine === false,
      items: entry.resources.items.snapshot as InventoryResourceSnapshot<Item>,
      locations: entry.resources.locations
        .snapshot as InventoryResourceSnapshot<Location>,
    };
    this.revision += 1;
    entry.consumers.forEach((_, listener) => listener());
  }
  private demanded(entry: Entry) {
    return new Set([...entry.consumers.values()].flat());
  }

  subscribe = (
    id: string,
    resources: readonly InventoryResource[],
    listener: () => void,
    itemSort?: InventoryItemSort,
  ) => {
    if (this.disposed) return () => undefined;
    const entry = this.entry(id);
    if (
      itemSort &&
      resources.includes('items') &&
      entry.itemSort !== itemSort
    ) {
      entry.itemSort = itemSort;
      const resource = entry.resources.items;
      if (entry.running.has('items') || resource.snapshot.nextCursor) {
        // A cursor belongs to one ordering. Fence it before starting a different sort.
        entry.resources.items = { ...empty(), version: resource.version + 1 };
        entry.dirty.add('items');
        this.wakeups.forEach((wake) => wake());
      }
    }
    // Each subscription owns its reference even when React supplies the same callback.
    const notify = () => listener();
    const first = !entry.consumers.size;
    entry.consumers.set(notify, resources);
    if (first && !entry.stops.length) this.attachAuthority(entry);
    else this.ensure(entry);
    this.dependencies.demandChanged();
    return () => {
      entry.consumers.delete(notify);
      if (!entry.consumers.size) {
        // React replaces subscriptions together when the selected scope changes.
        // Keep unchanged collections and their in-flight reads through that turn.
        void Promise.resolve().then(() => {
          if (entry.consumers.size || this.disposed) return;
          this.stopAuthority(entry);
          entry.snapshot = { ...entry.snapshot, status: 'loading' };
        });
      }
      this.dependencies.demandChanged();
    };
  };

  private stopAuthority(entry: Entry) {
    entry.epoch += 1;
    this.wakeups.forEach((wake) => wake());
    entry.stops.forEach((stop) => stop());
    entry.stops = [];
    entry.running.forEach((kind) => entry.dirty.add(kind));
    entry.running.clear();
    entry.wanted.clear();
    entry.pending = undefined;
    kinds.forEach((kind) => {
      entry.resources[kind].snapshot = {
        ...entry.resources[kind].snapshot,
        busy: false,
        refreshing: false,
      };
    });
  }
  private attachAuthority(entry: Entry) {
    this.stopAuthority(entry);
    const epoch = entry.epoch;
    entry.membershipReady = false;
    entry.collectionReady = false;
    entry.authorityFailed = false;
    entry.snapshot = { ...entry.snapshot, status: 'loading' };
    const failed = () => {
      if (this.disposed || entry.epoch !== epoch) return;
      entry.authorityFailed = true;
      this.reconcileAuthority(entry);
    };
    entry.stops = [
      this.dependencies.membership(
        entry.id,
        (value) => {
          if (this.disposed || entry.epoch !== epoch) return;
          entry.membership = value;
          entry.membershipReady = true;
          this.reconcileAuthority(entry);
        },
        failed,
      ),
      this.dependencies.collection(
        entry.id,
        (value) => {
          if (this.disposed || entry.epoch !== epoch) return;
          entry.collection = value;
          entry.collectionReady = true;
          this.reconcileAuthority(entry);
        },
        failed,
      ),
    ];
    this.publish(entry);
  }
  private clear(entry: Entry) {
    entry.hydrated = true;
    void this.dependencies.cache?.remove(entry.id);
    kinds.forEach((kind) => {
      const resource = entry.resources[kind];
      entry.resources[kind] = { ...empty(), version: resource.version + 1 };
    });
    entry.dirty.clear();
    entry.wanted.clear();
    // A superseded read still owns `running` until it settles. Preserve demand
    // for its replacement; drain waits for both completion and valid authority.
    this.demanded(entry).forEach((kind) => entry.wanted.add(kind));
  }
  private reconcileAuthority(entry: Entry) {
    const { membership, collection } = entry;
    const unavailable =
      (entry.membershipReady &&
        (!membership || membership.status !== 'active')) ||
      (entry.collectionReady &&
        (!collection || collection.lifecycle === 'deleted'));
    if (entry.authorityFailed || unavailable) {
      this.clear(entry);
      entry.snapshot = {
        ...entry.snapshot,
        status: unavailable ? 'unavailable' : 'error',
        summary: undefined,
      };
      this.publish(entry);
      return;
    }
    if (
      !entry.membershipReady ||
      !entry.collectionReady ||
      !membership ||
      !collection
    )
      return;
    const identity = JSON.stringify([
      membership.membershipVersion,
      membership.role,
      collection.lifecycleVersion,
      collection.lifecycle,
      collection.ownerUserId,
    ]);
    if (entry.identity && identity !== entry.identity) this.clear(entry);
    entry.identity = identity;
    entry.snapshot = {
      ...entry.snapshot,
      status: 'ready',
      summary: {
        collectionId: entry.id,
        name: collection.name,
        role: membership.role,
        membershipVersion: membership.membershipVersion,
        lifecycle: collection.lifecycle,
        lifecycleVersion: collection.lifecycleVersion,
        ownerUserId: collection.ownerUserId,
        ...(collection.activeTransferId
          ? { activeTransferId: collection.activeTransferId }
          : {}),
      },
    };
    this.publish(entry);
    this.ensure(entry);
  }

  private ensure(entry: Entry) {
    if (entry.snapshot.status !== 'ready') return;
    if (!entry.hydrated && this.dependencies.cache) {
      if (!entry.hydrating) {
        const epoch = entry.epoch;
        const identity = entry.identity;
        entry.hydrating = this.dependencies.cache
          .read(entry.id)
          .then((cached) => {
            if (
              this.disposed ||
              entry.epoch !== epoch ||
              entry.identity !== identity ||
              entry.snapshot.status !== 'ready'
            )
              return;
            let patched = false;
            if (cached && cached.identity === identity)
              kinds.forEach((kind) => {
                const saved = cached[kind];
                const resource = entry.resources[kind];
                if (
                  !saved ||
                  resource.snapshot.initialized ||
                  entry.dirty.has(kind)
                )
                  return;
                // Mutations received while disk was opening take precedence over its snapshot.
                patched ||= resource.patches.size > 0;
                resource.snapshot = {
                  ...resource.snapshot,
                  records: mergeRecords(saved.records, resource.patches),
                  initialized: true,
                  dataRevision: this.revision + 1,
                };
                resource.updatedAt = saved.completedAt;
                if (!entry.running.has(kind)) resource.patches.clear();
              });
            if (patched) this.persist(entry);
          })
          .catch(() => undefined)
          .finally(() => {
            entry.hydrating = undefined;
            if (entry.epoch === epoch) entry.hydrated = true;
            this.publish(entry);
            if (!this.disposed) this.ensure(entry);
          });
      }
      return;
    }
    this.demanded(entry).forEach((kind) => {
      if (entry.running.has(kind)) return;
      const resource = entry.resources[kind];
      if (resource.snapshot.failed) return;
      if (!resource.snapshot.initialized) entry.wanted.add(kind);
      else if (Date.now() - resource.updatedAt >= INVENTORY_FRESH_MS)
        entry.dirty.add(kind);
    });
    void this.drain(entry);
  }

  loadMore = (id: string, resources: readonly InventoryResource[]) => {
    const entry = this.entry(id);
    resources.forEach((kind) => {
      const snapshot = entry.resources[kind].snapshot;
      if (
        !entry.running.has(kind) &&
        !snapshot.failed &&
        (!snapshot.initialized || snapshot.nextCursor)
      )
        entry.wanted.add(kind);
    });
    return this.drain(entry);
  };
  refresh = (
    id: string,
    resources: readonly InventoryResource[] = kinds,
    deletedIds: Partial<Record<InventoryResource, readonly string[]>> = {},
  ) => {
    const entry = this.entries.get(id);
    if (!entry || this.disposed) return;
    resources.forEach((kind) => {
      const resource = entry.resources[kind];
      resource.version += 1;
      resource.snapshot = {
        ...resource.snapshot,
        failed: false,
        records: resource.snapshot.records.filter(
          (record) => !deletedIds[kind]?.includes(record.id),
        ),
      };
      entry.dirty.add(kind);
    });
    this.wakeups.forEach((wake) => wake());
    this.publish(entry);
    void this.drain(entry);
  };
  reconcile = (id: string, resources: readonly InventoryResource[] = kinds) => {
    const entry = this.entries.get(id);
    if (!entry || entry.pending || entry.hydrating) return;
    this.refresh(id, resources);
  };
  refreshActive = (minAgeMs = INVENTORY_FRESH_MS) => {
    this.resume();
    if (!canLoad()) return;
    this.entries.forEach((entry) => {
      if (
        !entry.consumers.size ||
        entry.snapshot.status !== 'ready' ||
        entry.pending ||
        entry.hydrating
      )
        return;
      const stale = [...this.demanded(entry)].filter(
        (kind) => Date.now() - entry.resources[kind].updatedAt >= minAgeMs,
      );
      if (stale.length) this.refresh(entry.id, stale);
    });
  };
  resume = () => {
    this.wakeups.forEach((wake) => wake());
    this.entries.forEach((entry) => {
      if (entry.consumers.size) this.publish(entry);
    });
  };

  private persist(entry: Entry) {
    if (
      !this.dependencies.cache ||
      !entry.identity ||
      entry.snapshot.status !== 'ready' ||
      this.disposed
    )
      return;
    const saved: CachedInventory = { identity: entry.identity };
    const items = entry.resources.items;
    const locations = entry.resources.locations;
    if (
      items.updatedAt &&
      items.snapshot.initialized &&
      !items.snapshot.nextCursor
    )
      saved.items = {
        records: items.snapshot.records as Item[],
        completedAt: items.updatedAt,
      };
    if (
      locations.updatedAt &&
      locations.snapshot.initialized &&
      !locations.snapshot.nextCursor
    )
      saved.locations = {
        records: locations.snapshot.records as Location[],
        completedAt: locations.updatedAt,
      };
    void this.dependencies.cache.write(entry.id, saved);
  }

  applyChange = (change: InventoryChange) => {
    const entry = this.entries.get(change.collectionId);
    if (!entry || this.disposed) return;
    const fallback: InventoryResource[] = [];
    for (const kind of change.resources || kinds) {
      const upserts =
        kind === 'items' ? change.upsertItems : change.upsertLocations;
      const deleted =
        kind === 'items' ? change.deletedItemIds : change.deletedLocationIds;
      const reload =
        kind === 'items' ? change.reloadItemIds : change.reloadLocationIds;
      if (!upserts && !deleted && !reload) {
        fallback.push(kind);
        continue;
      }
      const resource = entry.resources[kind];
      const apply = (id: string, record: Item | Location | undefined) => {
        resource.patches.set(id, record);
        resource.snapshot = {
          ...resource.snapshot,
          records: mergeRecords(
            resource.snapshot.records,
            new Map([[id, record]]),
          ),
          dataRevision: this.revision + 1,
        };
        this.publish(entry);
        this.persist(entry);
        if (
          !entry.running.has(kind) &&
          !entry.hydrating &&
          resource.snapshot.initialized
        )
          resource.patches.clear();
      };
      // Fence older targeted reads, including reads for an item just deleted.
      [
        ...(upserts || []).map((record) => record.id),
        ...(deleted || []),
      ].forEach((id) =>
        resource.patchVersions.set(
          id,
          (resource.patchVersions.get(id) || 0) + 1,
        ),
      );
      upserts?.forEach((record) => apply(record.id, record));
      deleted?.forEach((id) => apply(id, undefined));
      reload?.forEach((id) => {
        if (
          !this.dependencies.record ||
          entry.snapshot.status !== 'ready' ||
          !entry.consumers.size
        ) {
          fallback.push(kind);
          return;
        }
        const epoch = entry.epoch;
        const version = (resource.patchVersions.get(id) || 0) + 1;
        resource.patchVersions.set(id, version);
        // Preserve the displayed value against a scan until the authoritative read completes.
        resource.patches.set(
          id,
          resource.snapshot.records.find((record) => record.id === id),
        );
        const current = () =>
          !this.disposed &&
          entry.epoch === epoch &&
          entry.resources[kind] === resource &&
          resource.patchVersions.get(id) === version;
        void this.withSlot(() => {
          if (!current()) return Promise.resolve(undefined);
          return this.dependencies.record!(entry.id, kind, id);
        })
          .then((record) => {
            if (!current() || entry.snapshot.status !== 'ready') return;
            apply(id, record);
          })
          .catch(() => {
            if (!current()) return;
            resource.patches.delete(id);
            resource.snapshot = { ...resource.snapshot, failed: true };
            this.publish(entry);
          });
      });
    }
    if (fallback.length) this.refresh(entry.id, [...new Set(fallback)]);
  };
  retry = (id: string) => {
    const entry = this.entry(id);
    if (!entry.consumers.size || this.disposed) return;
    if (entry.snapshot.status !== 'ready') this.attachAuthority(entry);
    else this.refresh(id, [...this.demanded(entry)]);
  };

  private async withSlot<T>(work: () => Promise<T>): Promise<T> {
    if (this.activeRequests >= 4)
      await new Promise<void>((resolve) => this.waiting.push(resolve));
    else this.activeRequests += 1;
    try {
      return await work();
    } finally {
      const next = this.waiting.shift();
      if (next) next();
      else this.activeRequests -= 1;
    }
  }

  private async request(
    entry: Entry,
    kind: InventoryResource,
    cursor: string | undefined,
    epoch: number,
    version: number,
  ) {
    const current = () =>
      !this.disposed &&
      entry.epoch === epoch &&
      entry.resources[kind].version === version;
    const wakeup = () =>
      new Promise<void>((resolve) => {
        const wake = () => {
          this.wakeups.delete(wake);
          resolve();
        };
        this.wakeups.add(wake);
      });
    while (!canLoad()) {
      if (!current()) return undefined;
      await wakeup();
    }
    return this.withSlot(async () => {
      if (!current()) return undefined;
      // Visibility can change while waiting for a concurrency slot.
      while (!canLoad()) {
        await wakeup();
        if (!current()) return undefined;
      }
      return kind === 'items' && entry.itemSort
        ? this.dependencies.page(entry.id, kind, cursor, entry.itemSort)
        : this.dependencies.page(entry.id, kind, cursor);
    });
  }

  private drain(entry: Entry): Promise<void> {
    if (entry.pending) return entry.pending;
    if (
      this.disposed ||
      !entry.consumers.size ||
      entry.snapshot.status !== 'ready'
    )
      return Promise.resolve();
    const demand = this.demanded(entry);
    const work = kinds.filter(
      (kind) =>
        demand.has(kind) && (entry.dirty.has(kind) || entry.wanted.has(kind)),
    );
    if (!work.length) return Promise.resolve();
    const epoch = entry.epoch;
    const plans = work.map((kind) => {
      const resource = entry.resources[kind];
      const replace =
        entry.dirty.delete(kind) || !resource.snapshot.initialized;
      entry.wanted.delete(kind);
      entry.running.add(kind);
      resource.snapshot = {
        ...resource.snapshot,
        busy: true,
        refreshing: replace && resource.snapshot.initialized,
      };
      return { kind, resource, replace, version: resource.version };
    });
    // Register the shared promise before emitting state or invoking a synchronous mock transport.
    entry.pending = Promise.resolve()
      .then(async () => {
        const results = await Promise.all(
          plans.map(async (plan) => {
            let cursor = plan.replace
              ? undefined
              : plan.resource.snapshot.nextCursor;
            const records: Array<Item | Location> = [];
            try {
              do {
                const page = await this.request(
                  entry,
                  plan.kind,
                  cursor,
                  epoch,
                  plan.version,
                );
                if (!page) return { plan };
                records.push(...page.records);
                cursor = page.nextCursor;
                if (
                  this.disposed ||
                  entry.epoch !== epoch ||
                  plan.resource.version !== plan.version ||
                  entry.snapshot.status !== 'ready'
                )
                  return { plan };
                // Publish cold pages progressively; a reconciliation retains the previous
                // complete inventory until every page has succeeded.
                if (!plan.resource.updatedAt) {
                  plan.resource.snapshot = {
                    ...plan.resource.snapshot,
                    records: mergeRecords(
                      [...plan.resource.snapshot.records, ...records],
                      plan.resource.patches,
                    ),
                    initialized: true,
                    nextCursor: cursor,
                    orderedThrough: cursor
                      ? page.records[page.records.length - 1]
                      : undefined,
                    dataRevision: this.revision + 1,
                  };
                  this.publish(entry);
                }
              } while (cursor);
              return { plan, records, cursor };
            } catch {
              return { plan, failed: true };
            }
          }),
        );
        if (this.disposed || entry.epoch !== epoch) return;
        results.forEach(({ plan, ...result }) => {
          entry.running.delete(plan.kind);
          const resource = entry.resources[plan.kind];
          if (
            resource.version !== plan.version ||
            entry.snapshot.status !== 'ready'
          )
            return;
          if ('failed' in result)
            resource.snapshot = {
              ...resource.snapshot,
              busy: false,
              refreshing: false,
              failed: true,
            };
          else if ('records' in result && result.records) {
            const records = plan.replace
              ? result.records
              : [...resource.snapshot.records, ...result.records];
            const nextRecords = mergeRecords(records, resource.patches);
            resource.patches.clear();
            const unchanged =
              nextRecords.length === resource.snapshot.records.length &&
              nextRecords.every(
                (record, index) => record === resource.snapshot.records[index],
              );
            resource.snapshot = {
              dataRevision: unchanged
                ? resource.snapshot.dataRevision
                : this.revision + 1,
              records: nextRecords,
              confirmedOnline:
                typeof navigator === 'undefined' || navigator.onLine !== false,
              nextCursor: result.cursor,
              initialized: true,
              busy: false,
              refreshing: false,
              failed: false,
            };
            resource.updatedAt = Date.now();
          }
        });
        this.publish(entry);
        this.persist(entry);
      })
      .finally(() => {
        if (this.disposed || entry.epoch !== epoch) return;
        entry.pending = undefined;
        return this.drain(entry);
      });
    this.publish(entry);
    return entry.pending;
  }

  dispose() {
    this.disposed = true;
    this.entries.forEach((entry) => {
      this.stopAuthority(entry);
      kinds.forEach((kind) => {
        entry.resources[kind] = empty();
      });
      entry.snapshot = {
        ...entry.snapshot,
        status: 'unavailable',
        summary: undefined,
      };
      this.publish(entry);
    });
  }
}
